import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSession } from '@/features/auth/useSession';
import { FullScreenSpinner } from '@/app/layouts/FullScreenSpinner';

/**
 * Google returns here with `?code=`. The auth client exchanges it because
 * `detectSessionInUrl` is on, so this screen only waits for the session to appear and
 * then gets out of the way.
 *
 * The timeout matters: without it, a failed exchange leaves the user staring at a
 * spinner forever with no way back to the login button.
 */
export function AuthCallbackScreen() {
  const { session, loading } = useSession();
  const navigate = useNavigate();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!loading && session) {
      // replace: the callback URL carries a one-time code and must not be re-enterable
      // with the back button.
      void navigate('/board', { replace: true });
    }
  }, [loading, session, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), 12_000);
    return () => clearTimeout(timer);
  }, []);

  if (timedOut && !session) {
    const description = new URLSearchParams(window.location.search).get('error_description');
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-sm font-medium">Sign-in did not complete.</p>
        {description && (
          <p className="max-w-sm text-xs" style={{ color: 'var(--text-muted)' }}>
            {description}
          </p>
        )}
        <button
          type="button"
          onClick={() => void navigate('/login', { replace: true })}
          className="rounded-lg border px-4 py-2 text-sm"
          style={{ borderColor: 'var(--border)' }}
        >
          Try again
        </button>
      </div>
    );
  }

  return <FullScreenSpinner label="Signing you in" />;
}
